/**
 * SOUND-652 — bulk MID import (CSV), the pure half.
 *
 * Everything the import modal decides lives here so it can be pinned by tests;
 * sound-app has no component harness (SOUND-679). The modal only renders.
 *
 * Contract: docs/plans/2026-10-30-sound-652-bulk-mid-import.md ("API contract").
 *
 * Three rules this file exists to keep:
 *
 * 1. EVERY VALUE IS A STRING. The parser runs with dynamicTyping off, and nothing
 *    here coerces. A MID like "019100114637" must reach the server byte for byte;
 *    the server owns coercion (numbers, booleans, dates).
 *
 * 2. EXPORT ROUND-TRIPS. The tab's Export writes identifiers as ="0123" so Excel
 *    keeps the zeros, prefixes formula-looking text with a quote, and labels its
 *    columns for humans ("Volume cap", "Effective"). The header mapping and
 *    `unwrapCellValue` undo exactly those, so export → edit → re-import works.
 *
 * 3. MAPPINGS ARE KEYED BY THE RAW VALUE THE ROW CARRIES. A row sends
 *    `processor: "KURV"`, and the server looks up `processorMappings["KURV"]`.
 *    The distinct values and the row values therefore go through the same
 *    `unwrapCellValue`, or a mapping would silently miss its rows.
 */

import Papa from "papaparse";
import { toCsv } from "utilities/csv";

/** Server rejects the whole request above this; the UI refuses it first. */
export const MAX_IMPORT_ROWS = 500;

export const REQUIRED_FIELDS = ["mid", "descriptor", "mcc", "caid", "processor"];

/**
 * Every field the ImportRow contract accepts, with the header spellings that
 * map to it. Spellings are compared after `normalizeHeader`, so case, spaces,
 * underscores, punctuation and a "(required)" / "*" marker don't matter.
 * Includes the tab's Export labels ("Name", "MID", "Volume cap", "Processor",
 * "Effective").
 */
export const IMPORT_FIELDS = [
  { field: "mid", label: "MID", aliases: ["mid", "midnumber", "merchantidnumber"] },
  { field: "descriptor", label: "Descriptor", aliases: ["descriptor", "billingdescriptor", "softdescriptor"] },
  { field: "mcc", label: "MCC", aliases: ["mcc", "mcccode", "merchantcategorycode"] },
  { field: "caid", label: "CAID", aliases: ["caid"] },
  { field: "processor", label: "Processor", aliases: ["processor", "processorname"] },
  { field: "dba", label: "DBA", aliases: ["dba", "dbaname"] },
  { field: "name", label: "Name", aliases: ["name", "midname"] },
  { field: "monthlyVolumeCap", label: "Volume cap", aliases: ["volumecap", "monthlyvolumecap", "monthlycap", "cap"] },
  { field: "isUncapped", label: "Uncapped", aliases: ["uncapped", "isuncapped"] },
  { field: "effectiveDate", label: "Effective", aliases: ["effective", "effectivedate"] },
  // Left out of the template (CPO, 2026-10-30) but still read if a file has it.
  { field: "chargebackThreshold", label: "Chargeback threshold", aliases: ["chargebackthreshold", "cbthreshold"], inTemplate: false },
  { field: "acquiringBank", label: "Acquiring bank", aliases: ["acquiringbank", "acquirer", "bank"] },
  { field: "bin", label: "BIN", aliases: ["bin", "acquirerbin"] },
  { field: "currencies", label: "Currencies", aliases: ["currencies", "currency"] },
  { field: "notes", label: "Notes", aliases: ["notes", "note"] },
];

/**
 * Headers that are RECOGNISED and deliberately NOT read, with the reason shown
 * to the user. Sound-owned is set per MID in the preview and never from the
 * sheet (CPO, 2026-10-30): a sheet cell used to override the UI toggle
 * silently. Listed here rather than left unknown so the user is told why.
 */
export const IGNORED_HEADERS = {
  soundowned: "set ownership per MID in the preview",
  issoundowned: "set ownership per MID in the preview",
};

const ALIAS_TO_FIELD = new Map();
for (const f of IMPORT_FIELDS) {
  for (const a of f.aliases) ALIAS_TO_FIELD.set(a, f.field);
}

const FIELD_LABEL = Object.fromEntries(IMPORT_FIELDS.map((f) => [f.field, f.label]));

/** "MID (required)" / "Volume Cap" / "acquiring_bank *" → "mid" / "volumecap" / "acquiringbank". */
export function normalizeHeader(header) {
  return String(header ?? "")
    .replace(/^﻿/, "")
    .toLowerCase()
    .replace(/\(required\)/g, "")
    .replace(/[^a-z0-9]/g, "");
}

/** The ImportRow field a header names, or null if it names none. */
export function fieldForHeader(header) {
  return ALIAS_TO_FIELD.get(normalizeHeader(header)) || null;
}

/**
 * Map a header row onto fields.
 * Returns { columns: [field|null per column], unknown: [header], duplicates: [header],
 *           ignored: [{ header, reason }], missingRequired: [field] }.
 * The FIRST column naming a field wins; later ones are reported, not merged.
 */
export function mapHeaders(headers = []) {
  const seen = new Set();
  const columns = [];
  const unknown = [];
  const duplicates = [];
  const ignored = [];
  for (const h of headers) {
    const field = fieldForHeader(h);
    const ignoredReason = IGNORED_HEADERS[normalizeHeader(h)];
    if (ignoredReason) {
      ignored.push({ header: String(h).trim(), reason: ignoredReason });
      columns.push(null);
    } else if (!field) {
      if (String(h ?? "").trim()) unknown.push(String(h).trim());
      columns.push(null);
    } else if (seen.has(field)) {
      duplicates.push(String(h).trim());
      columns.push(null);
    } else {
      seen.add(field);
      columns.push(field);
    }
  }
  const missingRequired = REQUIRED_FIELDS.filter((f) => !seen.has(f));
  return { columns, unknown, duplicates, ignored, missingRequired };
}

const FORMULA_TRIGGERS = ["=", "+", "-", "@", "\t", "\r"];

/**
 * One cell, back to the value a human meant.
 *  - `="0123"` (what Export and the template write) → `0123`, inner "" → ".
 *  - `'=SUM` (Export's formula-injection guard) → `=SUM`. Only a quote followed
 *    by a trigger character is stripped; any other leading quote is data.
 *  - Surrounding whitespace is trimmed. Nothing else changes: no number
 *    coercion, so leading zeros survive.
 */
export function unwrapCellValue(value) {
  if (value === null || value === undefined) return "";
  let str = String(value).trim();
  const wrapped = /^="([\s\S]*)"$/.exec(str);
  if (wrapped) return wrapped[1].replace(/""/g, '"').trim();
  if (str.length > 1 && str[0] === "'" && FORMULA_TRIGGERS.includes(str[1])) {
    str = str.slice(1);
  }
  return str;
}

const UNCAPPED_WORDS = new Set(["uncapped", "unlimited"]);

/**
 * Turn mapped cells into a contract ImportRow.
 * Required fields are always present (possibly "", which the server reports as
 * an error with a reason). Optional fields are sent only when non-blank.
 * Export writes "Uncapped" in the Volume cap column, so that word becomes
 * `isUncapped: "true"` rather than a cap the server cannot parse.
 */
export function toImportRow(cells, columns, rowNumber) {
  const row = { rowNumber };
  for (const f of REQUIRED_FIELDS) row[f] = "";
  columns.forEach((field, i) => {
    if (!field) return;
    const v = unwrapCellValue(cells[i]);
    if (REQUIRED_FIELDS.includes(field)) row[field] = v;
    else if (v !== "") row[field] = v;
  });
  if (row.monthlyVolumeCap && UNCAPPED_WORDS.has(row.monthlyVolumeCap.toLowerCase())) {
    delete row.monthlyVolumeCap;
    if (row.isUncapped === undefined) row.isUncapped = "true";
  }
  return row;
}

const isBlankRow = (cells) => cells.every((c) => unwrapCellValue(c) === "");

/**
 * Parse CSV text into ImportRows.
 * Returns { rows, mapping, error } — `error` is a sentence for the user, set when
 * the file can't be imported at all (empty, no data rows, missing required
 * columns, over the cap). `rowNumber` is the 1-based data row, header not
 * counted, blank lines skipped.
 */
export function parseImportCsv(text) {
  const empty = { rows: [], mapping: null, error: null };
  const src = String(text ?? "").replace(/^﻿/, "");
  if (!src.trim()) return { ...empty, error: "The file is empty." };

  const parsed = Papa.parse(src, { header: false, dynamicTyping: false, skipEmptyLines: "greedy" });
  const table = (parsed.data || []).filter((r) => Array.isArray(r) && !isBlankRow(r));
  if (!table.length) return { ...empty, error: "The file is empty." };

  const mapping = mapHeaders(table[0]);
  if (mapping.missingRequired.length) {
    return {
      ...empty,
      mapping,
      error: `Missing required column${mapping.missingRequired.length > 1 ? "s" : ""}: ${mapping.missingRequired
        .map((f) => FIELD_LABEL[f])
        .join(", ")}.`,
    };
  }

  const dataRows = table.slice(1);
  if (!dataRows.length) return { ...empty, mapping, error: "The file has a header row but no data rows." };
  if (dataRows.length > MAX_IMPORT_ROWS) {
    return {
      ...empty,
      mapping,
      error: `The file has ${dataRows.length.toLocaleString()} rows. The limit is ${MAX_IMPORT_ROWS} per import — split it and import each part.`,
    };
  }

  const rows = dataRows.map((cells, i) => toImportRow(cells, mapping.columns, i + 1));
  return { rows, mapping, error: null };
}

/** Distinct non-blank values of one field, in first-seen order. Case-sensitive on purpose. */
export function distinctValues(rows = [], field) {
  const out = [];
  const seen = new Set();
  for (const r of rows) {
    const v = r?.[field];
    if (typeof v !== "string" || v === "" || seen.has(v)) continue;
    seen.add(v);
    out.push(v);
  }
  return out;
}

/** How many rows carry each value of a field. */
export function valueRowCounts(rows = [], field) {
  const counts = {};
  for (const r of rows) {
    const v = r?.[field];
    if (typeof v === "string" && v !== "") counts[v] = (counts[v] || 0) + 1;
  }
  return counts;
}

/** POST /match body. */
export function matchRequestBody(rows) {
  return { processors: distinctValues(rows, "processor"), dbas: distinctValues(rows, "dba") };
}

/**
 * Starting picker state from a /match response: the proposed match, or unset
 * ("" for a processor, null = No DBA for a DBA). A human still confirms it.
 */
export function initialMappings(match) {
  const processorMappings = {};
  for (const p of match?.processors || []) {
    processorMappings[p.value] = p.proposed?.processorId || "";
  }
  const dbaMappings = {};
  for (const d of match?.dbas || []) {
    dbaMappings[d.value] = d.proposed?.dbaId || null;
  }
  return { processorMappings, dbaMappings };
}

/** Distinct processor values with no valid pick. Empty ⇒ the gate opens. */
export function unmappedProcessors(rows, processorMappings = {}, options = []) {
  const valid = new Set((options || []).map((o) => o.processorId));
  return distinctValues(rows, "processor").filter((v) => {
    const id = processorMappings[v];
    return !id || !valid.has(id);
  });
}

/** The "every distinct processor is mapped" gate. */
export function allProcessorsMapped(rows, processorMappings, options) {
  return unmappedProcessors(rows, processorMappings, options).length === 0;
}

/** A row with any ownership value removed — the sheet never decides ownership. */
function withoutOwnership(row) {
  const { isSoundOwned, ...rest } = row || {};
  return rest;
}

/**
 * POST /preview body. Mappings are restricted to the values the rows carry, and
 * every distinct DBA value is present (null = No DBA) so the server never has to
 * guess what an absent key meant.
 *
 * Ownership: rows carry NONE (stripped defensively even though the parser never
 * reads the column), and the import-level `isSoundOwned` is always `false` —
 * the server's fallback. Per-row choices are attached only at commit.
 */
export function previewRequestBody({ fileName, rows = [], processorMappings = {}, dbaMappings = {} }) {
  const pm = {};
  for (const v of distinctValues(rows, "processor")) pm[v] = processorMappings[v];
  const dm = {};
  for (const v of distinctValues(rows, "dba")) dm[v] = dbaMappings[v] || null;
  return {
    fileName: fileName || "import.csv",
    rows: rows.map(withoutOwnership),
    processorMappings: pm,
    dbaMappings: dm,
    isSoundOwned: false,
  };
}

/** Row numbers that may carry an ownership toggle: create rows only. */
export function ownershipEligibleRows(previewRows = []) {
  return (previewRows || []).filter((r) => r.label === "create").map((r) => r.rowNumber);
}

/** The header "all on / all off" control: every create row set to `value`. */
export function setAllOwnership(previewRows, value) {
  const out = {};
  for (const n of ownershipEligibleRows(previewRows)) out[n] = value === true;
  return out;
}

/** "all" | "none" | "some" — drives the header control's checked/indeterminate state. */
export function ownershipState(previewRows, ownership = {}) {
  const eligible = ownershipEligibleRows(previewRows);
  const on = eligible.filter((n) => ownership[n] === true).length;
  if (!eligible.length || on === 0) return "none";
  return on === eligible.length ? "all" : "some";
}

/**
 * POST / (commit) body: the preview body plus the possible-duplicate rows a
 * human ticked. Only ticks on create rows that ARE possible duplicates count.
 */
export function commitRequestBody(previewArgs, accepted = {}, previewRows = [], ownership = {}) {
  const eligible = new Set(
    (previewRows || []).filter((r) => r.label === "create" && r.possibleDuplicate).map((r) => r.rowNumber)
  );
  const acceptPossibleDuplicates = Object.keys(accepted)
    .filter((k) => accepted[k])
    .map(Number)
    .filter((n) => eligible.has(n))
    .sort((a, b) => a - b);
  // Every row gets a REAL boolean (the server rejects anything else): true only
  // for a create row a human toggled on in the preview.
  const eligibleOwnership = new Set(ownershipEligibleRows(previewRows));
  const body = previewRequestBody(previewArgs);
  body.rows = body.rows.map((row) => ({
    ...row,
    isSoundOwned: eligibleOwnership.has(row.rowNumber) && ownership[row.rowNumber] === true,
  }));
  return { ...body, acceptPossibleDuplicates };
}

/** Rows that would be created and need no human decision, plus ticked duplicates. */
export function createCount(previewRows = [], accepted = {}) {
  return (previewRows || []).filter(
    (r) => r.label === "create" && (!r.possibleDuplicate || accepted[r.rowNumber])
  ).length;
}

export const PREVIEW_LABELS = {
  create: { label: "Create", tone: "success" },
  exists: { label: "Already here", tone: "muted" },
  conflict: { label: "Conflict", tone: "destructive" },
  error: { label: "Error", tone: "warning" },
};

export function filterPreviewRows(rows = [], label) {
  if (!label) return rows || [];
  return (rows || []).filter((r) => r.label === label);
}

/**
 * The outcome a result row reports. The contract leaves a skipped possible
 * duplicate's label unstated; a create row with no midId was not written.
 */
export function resultOutcome(row) {
  if (row?.midId) return "created";
  if (row?.label === "create") return row.possibleDuplicate ? "skipped (possible duplicate)" : "not created";
  return PREVIEW_LABELS[row?.label]?.label.toLowerCase() || row?.label || "";
}

/** A created MID's ownership as the server stored it; blank for rows not created. */
export function ownershipLabel(row) {
  if (!row?.midId) return "";
  return row.resolved?.isSoundOwned === true ? "Yes" : "No";
}

/** Column model for the per-row report, fed to utilities/csv toCsv. */
export const REPORT_COLUMNS = [
  { key: "rowNumber", header: "Row" },
  { key: "outcome", header: "Outcome", exportValue: resultOutcome },
  { key: "mid", header: "MID", exportAs: "text", exportValue: (r) => r.resolved?.mid ?? "" },
  { key: "descriptor", header: "Descriptor", exportValue: (r) => r.resolved?.descriptor ?? "" },
  { key: "processorName", header: "Processor", exportValue: (r) => r.resolved?.processorName ?? "" },
  { key: "dbaName", header: "DBA", exportValue: (r) => r.resolved?.dbaName ?? "" },
  { key: "midId", header: "MID record id", exportValue: (r) => r.midId || "" },
  { key: "isSoundOwned", header: "Sound-owned", exportValue: ownershipLabel },
  {
    key: "possibleDuplicate",
    header: "Possible duplicate of",
    exportAs: "text",
    exportValue: (r) => r.possibleDuplicate?.mid ?? "",
  },
  { key: "reasons", header: "Reasons", exportValue: (r) => (r.reasons || []).join("; ") },
];

/**
 * The downloadable template: a HEADER ROW ONLY, same columns the importer reads.
 * Deliberately no example row — a plausible fake MID that imports cleanly when
 * someone forgets to delete it is exactly the silent bad data this feature
 * exists to prevent. Required fields are named beside the download link, not in
 * the header, so the headers stay plain.
 */
export const TEMPLATE_COLUMNS = IMPORT_FIELDS.filter((f) => f.inTemplate !== false).map((f) => ({
  key: f.field,
  header: f.label,
}));

/** The template document: header row only. */
export function templateCsv() {
  return toCsv([], TEMPLATE_COLUMNS);
}

export const TEMPLATE_FILENAME = "mid-import-template.csv";
